import { resetLogin } from '@/account/ducks/login'
import { logout } from '@/account/services/auth'
import { destroySession } from '@/account/services/session'
import { clear, toggleSidebar } from '@/app-reducer'
import { Icon } from '@/common/components/Icon'
import { LogoutConfirmationModal } from '@/common/components/LogoutConfirmationModal'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'
import { Globe } from 'lucide-react'
import { useAppDispatch, useAppSelector } from '@/store'
import { FC, Fragment, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

interface HeaderProps {
  authPage?: boolean
  actions?: React.ReactNode
  pageTitle?: string
  pageSubtitle?: string
  breadcrumbs?: string[]
  hideFestivalName?: boolean
}

export const Header: FC<HeaderProps> = ({
  authPage = false,
  actions,
  pageTitle,
  pageSubtitle,
  breadcrumbs,
  hideFestivalName = false,
}) => {
  const navigate = useNavigate()

  const dispatch = useAppDispatch()
  const { editionId } = useParams()

  const { user, festivalName, festivals } = useAppSelector((s) => s.app)
  const [confirmLogout, setConfirmLogout] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)

  const getUserInitials = () => {
    if (!user) return '?'
    const firstName = user.firstName || ''
    const lastName = user.lastName || ''
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase()
    }
    if (firstName) return firstName.substring(0, 2).toUpperCase()
    if (user.email) return user.email.substring(0, 2).toUpperCase()
    return '?'
  }

  const getUserDisplayName = () => {
    if (!user) return ''
    const firstName = user.firstName || ''
    const lastName = user.lastName || ''
    if (firstName && lastName) return `${firstName} ${lastName}`
    if (firstName) return firstName
    return user.email
  }

  const activeFestival = festivals.find((f) => f.platformName === festivalName)
  const activeEdition = activeFestival?.editions.find((e) => e.id === editionId)

  const onLogout = async () => {
    setLoggingOut(true)
    try {
      const { status } = await logout()
      if (status === 200) {
        setConfirmLogout(false)
        navigate('/account/login', { replace: true })
        destroySession()
        dispatch(clear())
        dispatch(resetLogin())
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoggingOut(false)
    }
  }

  const UserProfileDropdown = () => {
    const { pathname } = useLocation()

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="flex items-center gap-3 px-4 py-2">
            <Avatar className="w-11 h-11 bg-monochrome-2">
              <AvatarFallback className="text-monochrome-1 text-xs font-semibold">
                {getUserInitials()}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col items-start">
              <span className="text-monochrome-1 text-button-m capitalize">
                {getUserDisplayName()}
              </span>
              <span className="text-accent-4 text-body-medium">
                {user?.superAdmin ? 'Admin' : 'User'}
              </span>
            </div>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="min-w-[200px] p-5 py-2 backdrop-blur-sm bg-black/60 border-2 border-monochrome-120">
          {/* Profile Menu Item */}
          <button
            onClick={() => navigate('/my-profile')}
            className={cn(
              'flex items-center w-full gap-[10px] transition-all cursor-pointer py-[15px] border-b',
              pathname === '/my-profile'
                ? 'text-accent-1 border-b-accent-1'
                : 'text-monochrome-1 border-b-monochrome-120 hover:text-monochrome-2',
            )}
          >
            <Icon
              icon="profile-2user"
              width="18"
              height="18"
              viewBox="0 0 20 20"
              className="text-accent-1"
              fill="none"
            />
            <span className="text-button-m flex-1 text-left">Profile</span>
          </button>

          {/* Logout Menu Item */}
          <button
            onClick={() => setConfirmLogout(true)}
            className="flex items-center w-full gap-[10px] transition-all cursor-pointer py-[15px] text-monochrome-1 hover:text-monochrome-2"
          >
            <Icon
              icon="logout"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              className="text-accent-3"
              fill="none"
            />
            <span className="text-button-m flex-1 text-left">Logout</span>
          </button>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  const SettingsDropdown = () => {
    const { pathname } = useLocation()
    const [isSettingsExpanded, setIsSettingsExpanded] = useState(false)
    const isSettingsActive = pathname.startsWith('/settings')
    const isGroupsActive = pathname.startsWith('/groups')

    useEffect(() => {
      if (isSettingsActive) {
        setIsSettingsExpanded(true)
      }
    }, [isSettingsActive])

    const settingsChildren = [
      { title: 'Festivals', path: '/settings/festivals' },
      { title: 'Admins', path: '/settings/admins' },
    ]

    return (
      <div className="flex items-center gap-2.5">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="secondary"
              className="group flex items-center gap-2.5 data-[state=open]:bg-gradient-6 border border-monochrome-1/20 [&_svg]:transition-transform [&_svg]:duration-200"
            >
              <span className="text-white font-semibold text-button-m">
                Global Settings
              </span>
              <Icon
                icon="settings-switches"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                className="text-white group-data-[state=open]:hidden"
              />
              <Icon
                icon="close"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                className="text-white hidden group-data-[state=open]:block"
              />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="min-w-[300px] p-5 backdrop-blur-sm bg-black/60 border-2 border-monochrome-120">
            {/* Settings Menu Item */}
            <div className="w-full">
              <button
                onClick={() => setIsSettingsExpanded(!isSettingsExpanded)}
                className={cn(
                  'flex items-center w-full gap-[10px] transition-all cursor-pointer py-[15px] border-b',
                  isSettingsActive
                    ? 'text-accent-1 border-b-accent-1'
                    : 'text-monochrome-1 border-b-monochrome-120 hover:text-monochrome-2',
                )}
              >
                <Icon
                  icon="setting-3"
                  width="18"
                  height="18"
                  viewBox="0 0 20 20"
                  className="text-accent-1"
                  fill="none"
                />
                <span className="text-button-m flex-1 text-left">Settings</span>
                <Icon
                  icon="chevron-down"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  className={cn(
                    'w-4 h-4 transition-transform duration-300',
                    isSettingsExpanded && 'rotate-180',
                  )}
                  fill="none"
                />
              </button>
              {isSettingsExpanded && (
                <div className="pl-2 pb-2">
                  {settingsChildren.map((child, index) => {
                    const isChildActive =
                      pathname === child.path ||
                      pathname.startsWith(child.path + '/')

                    return (
                      <button
                        key={`child-${index}`}
                        onClick={() => navigate(child.path)}
                        className={cn(
                          'flex items-center w-full py-2 pl-3 rounded-lg text-sm transition-colors mt-1',
                          isChildActive
                            ? 'text-accent-1 bg-white/5'
                            : 'text-white/70 hover:text-white hover:bg-white/5',
                        )}
                      >
                        {child.title}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Groups Menu Item */}
            <button
              onClick={() => navigate('/groups')}
              className={cn(
                'flex items-center w-full gap-[10px] transition-all cursor-pointer py-[15px] border-b',
                isGroupsActive
                  ? 'text-accent-1 border-b-accent-1'
                  : 'text-monochrome-1 border-b-monochrome-120 hover:text-monochrome-2',
              )}
            >
              <Icon
                icon="profile-2user"
                width="18"
                height="18"
                viewBox="0 0 20 20"
                className="text-accent-1"
                fill="none"
              />
              <span className="text-button-m flex-1 text-left">Groups</span>
            </button>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    )
  }

  return (
    <header
      className={cn(
        'z-50 shadow-sm py-4 fixed top-0 left-0 right-0',
        authPage && 'px-5 lg:px-0 container border-b border-monochrome-120',
        !authPage && 'px-5',
      )}
    >
      <div className="flex items-center justify-between h-14">
        {/* Left Section - Logo & Menu */}
        <div className="flex items-center gap-4">
          {!authPage && (
            <>
              <button
                onClick={() => dispatch(toggleSidebar())}
                className="flex items-center justify-center w-11 h-11 bg-gradient-6 border border-white/20 rounded-3xl hover:bg-white/30 transition-colors"
                aria-label="Toggle sidebar"
              >
                <Icon
                  icon="sidebar-left"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-white"
                />
              </button>

              <UserProfileDropdown />
            </>
          )}

          {authPage && (
            <div
              className="flex items-center cursor-pointer"
              onClick={() => navigate('/')}
            >
              <Icon
                icon="rhuna"
                width="122px"
                height="37"
                viewBox="0 0 122 37"
              />
            </div>
          )}
        </div>

        <div className="fixed top-4 h-14 left-1/2 -translate-x-1/2 flex items-center gap-3 z-40">
          <div className="flex items-center gap-3">
            {(() => {
              const crumbs =
                breadcrumbs ??
                ([pageTitle, pageSubtitle].filter(Boolean) as string[])
              const items = [
                hideFestivalName ? null : activeEdition?.name,
                ...crumbs,
              ].filter(Boolean) as string[]
              return items.map((item, i) => (
                <Fragment key={i}>
                  {i > 0 && <span className="text-monochrome-2">|</span>}
                  <span
                    className={cn(
                      'text-subtitle-m',
                      i === items.length - 1
                        ? 'text-monochrome-1'
                        : 'text-monochrome-2',
                    )}
                  >
                    {item}
                  </span>
                </Fragment>
              ))
            })()}
          </div>
          {actions && (
            <div className="flex items-center">
              {breadcrumbs && breadcrumbs?.length > 0 && (
                <span className="text-monochrome-2 me-3">|</span>
              )}
              <div className="flex items-center gap-3">{actions}</div>
            </div>
          )}
        </div>

        {/* WL Settings + Settings Dropdown - top right */}
        {user?.superAdmin && !authPage && (
          <div className="flex items-center gap-2.5">
            <Button
              variant="secondary"
              onClick={() => navigate('/wl-settings')}
              className="group flex items-center gap-2.5 border border-monochrome-1/20"
            >
              <span className="text-white font-semibold text-button-m">
                WL Settings
              </span>
              <Icon
                icon="globe"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                className="text-white"
              />
            </Button>
            <SettingsDropdown />
          </div>
        )}
      </div>

      <LogoutConfirmationModal
        setLogoutConfirmationModal={setConfirmLogout}
        showLogoutConfirmationModal={confirmLogout}
        onSuccess={onLogout}
        loading={loggingOut}
      />
    </header>
  )
}

export default Header
